import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { AlertTriangle, CalendarDays, CreditCard, HandCoins, MapPin, Truck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useBasket } from "@/lib/BasketContext";
import { api, formatApiError, gbp } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { inputClass, labelClass } from "@/lib/constants";
import { useSite } from "@/lib/SiteContext";

const dayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/London" });
const cutoffFmt = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", timeZone: "Europe/London" });

let stripePromise;
const getStripe = (key) => {
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
};

function PaymentForm({ orderResult, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");
    const returnUrl = `${window.location.origin}/order/${orderResult.order_number}?t=${orderResult.access_token}`;
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });
    if (result.error) {
      setError(result.error.message || "Payment failed — please try another card.");
      setBusy(false);
    } else {
      onPaid();
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="payment-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm font-semibold text-red-300" role="alert" data-testid="payment-error">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || busy}
        className="w-full rounded-full bg-gold px-8 py-3.5 text-base font-bold text-choco transition-colors hover:bg-gold-hover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus active:scale-[0.98]"
        data-testid="pay-now-button"
      >
        {busy ? "Processing…" : `Pay ${gbp(orderResult.total_pence)}`}
      </button>
      <p className="text-center text-xs text-cream-muted">
        Secure payment by Stripe. We never see or store your card details.
      </p>
    </form>
  );
}

export default function Checkout() {
  const { items, clear } = useBasket();
  const { site } = useSite();
  const business = site?.business || {};
  const navigate = useNavigate();
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [address, setAddress] = useState({ line1: "", line2: "", city: "", postcode: "" });
  const [fulfilment, setFulfilment] = useState("collection");
  const [slotId, setSlotId] = useState(null);
  const [quote, setQuote] = useState(null);
  const [slotsData, setSlotsData] = useState(null);
  const [postcodeStatus, setPostcodeStatus] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [allergenAck, setAllergenAck] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [orderResult, setOrderResult] = useState(null);
  const [checkoutKey] = useState(() => {
    let key = sessionStorage.getItem("bbcr_checkout_key");
    if (!key) {
      key = crypto.randomUUID() + crypto.randomUUID();
      sessionStorage.setItem("bbcr_checkout_key", key);
    }
    return key;
  });

  const getQuote = async (overrides = {}) => {
    try {
      const res = await api.post("/public/checkout/quote", {
        items,
        fulfilment,
        postcode: fulfilment === "delivery" ? address.postcode || null : null,
        slot_id: slotId,
        ...overrides,
      });
      setQuote(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (items.length) getQuote({ slot_id: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, fulfilment]);

  useEffect(() => {
    setSlotId(null);
    setSlotsData(null);
    if (fulfilment === "delivery") setPaymentMethod("stripe");
    api.get(`/public/slots?type=${fulfilment}`).then((r) => setSlotsData(r.data)).catch(() => setSlotsData({ weeks: [] }));
  }, [fulfilment]);

  const checkPostcode = async () => {
    setBusy(true);
    const q = await getQuote({ postcode: address.postcode });
    setPostcodeStatus(q?.delivery || null);
    setBusy(false);
  };

  const chooseSlot = async (id) => {
    setSlotId(id);
    await getQuote({ slot_id: id });
  };

  const allAllergens = useMemo(() => [...new Set((quote?.items || []).flatMap((i) => i.allergens || []))], [quote]);
  const allMayContain = useMemo(() => [...new Set((quote?.items || []).flatMap((i) => i.may_contain || []))], [quote]);
  const blockers = quote?.blockers || [];
  const deliveryOk = fulfilment === "collection" || postcodeStatus?.eligible === true;
  const formReady =
    details.name.trim().length >= 2 &&
    /.+@.+\..+/.test(details.email) &&
    details.phone.trim().length >= 7 &&
    slotId &&
    deliveryOk &&
    allergenAck &&
    !quote?.checkout_blocked &&
    (fulfilment === "collection" || (address.line1.trim() && address.city.trim()));

  const placeOrder = async () => {
    setError("");
    setBusy(true);
    try {
      const res = await api.post("/public/orders", {
        checkout_key: checkoutKey,
        customer: { name: details.name, email: details.email, phone: details.phone },
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty })),
        fulfilment,
        address: fulfilment === "delivery" ? address : null,
        slot_id: slotId,
        payment_method: paymentMethod,
        notes: details.notes,
        allergen_acknowledged: allergenAck,
        marketing_consent: marketing,
      });
      const data = res.data;
      if (data.payment) {
        setOrderResult({ ...data, total_pence: quote.total_pence });
        window.scrollTo({ top: 0 });
      } else {
        sessionStorage.removeItem("bbcr_checkout_key");
        clear();
        navigate(`/order/${data.order_number}?t=${data.access_token}`);
      }
    } catch (e) {
      setError(formatApiError(e));
      window.scrollTo({ top: 0 });
    } finally {
      setBusy(false);
    }
  };

  const onPaid = () => {
    sessionStorage.removeItem("bbcr_checkout_key");
    clear();
    navigate(`/order/${orderResult.order_number}?t=${orderResult.access_token}`);
  };

  if (!items.length && !orderResult) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-3xl font-bold text-cream-text">Your basket is empty</h1>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-gold px-7 py-3 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="checkout-shop-link">
          Shop This Week's Bakes
        </Link>
      </div>
    );
  }

  if (orderResult?.payment) {
    return (
      <div className="mx-auto max-w-xl px-4 py-12 sm:py-16" data-testid="payment-step">
        <Reveal>
          <div className="rounded-3xl border border-choco-line bg-choco-light p-6 sm:p-8">
            <h1 className="flex items-center gap-2 font-serif text-2xl font-bold text-cream-text">
              <CreditCard className="h-6 w-6 text-gold" aria-hidden="true" /> Pay securely
            </h1>
            <p className="mt-2 text-sm text-cream-muted">
              Order {orderResult.order_number} · {gbp(orderResult.total_pence)}
            </p>
            <div className="mt-6 rounded-2xl bg-choco p-5">
              <Elements stripe={getStripe(orderResult.payment.publishable_key)} options={{ clientSecret: orderResult.payment.client_secret, appearance: { theme: "night", variables: { colorPrimary: "#E87A24", colorBackground: "#1A0E0A", colorText: "#F8F3ED", borderRadius: "12px" } } }}>
                <PaymentForm orderResult={orderResult} onPaid={onPaid} />
              </Elements>
            </div>
          </div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 pb-28 sm:px-6 sm:py-16 lg:px-8 md:pb-16" data-testid="checkout-page">
      <Reveal>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl">Checkout</h1>
        <p className="mt-2 text-sm text-cream-muted">Guest checkout — no account needed.</p>
      </Reveal>

      {error && <p className="mt-4 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm font-semibold text-red-300" role="alert" data-testid="checkout-error">{error}</p>}

      {quote?.issues?.length > 0 && (
        <div className="mt-6 space-y-2 rounded-3xl border border-amber-500/50 bg-amber-500/10 p-5" data-testid="checkout-issues">
          {quote.issues.map((issue, i) => (
            <p key={i} className="flex items-start gap-2 text-sm text-amber-200" data-testid={`checkout-issue-${i}`}>
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {issue.message}
            </p>
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-5">
        <div className="space-y-8 lg:col-span-3">
          <Reveal>
            <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="checkout-details">
              <h2 className="font-serif text-xl font-semibold text-cream-text">Your details</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="co-name" className={labelClass}>Full name *</label>
                  <input id="co-name" value={details.name} onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))} className={inputClass} data-testid="checkout-name-input" />
                </div>
                <div>
                  <label htmlFor="co-email" className={labelClass}>Email *</label>
                  <input id="co-email" type="email" value={details.email} onChange={(e) => setDetails((d) => ({ ...d, email: e.target.value }))} className={inputClass} data-testid="checkout-email-input" />
                </div>
                <div>
                  <label htmlFor="co-phone" className={labelClass}>Telephone *</label>
                  <input id="co-phone" type="tel" value={details.phone} onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))} className={inputClass} data-testid="checkout-phone-input" />
                </div>
                <div>
                  <label htmlFor="co-notes" className={labelClass}>Order notes</label>
                  <input id="co-notes" value={details.notes} onChange={(e) => setDetails((d) => ({ ...d, notes: e.target.value }))} className={inputClass} data-testid="checkout-notes-input" />
                </div>
              </div>
            </section>
          </Reveal>

          <Reveal>
            <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="checkout-fulfilment">
              <h2 className="font-serif text-xl font-semibold text-cream-text">Delivery or collection?</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  ["collection", "Collection", "Free — collect from our Redditch bakery (details on your order confirmation)", MapPin, "fulfilment-collection"],
                  ["delivery", "Local delivery", "Saturday or Sunday mornings — fee confirmed by postcode before you pay", Truck, "fulfilment-delivery"],
                ].map(([value, title, sub, Icon, testid]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFulfilment(value)}
                    aria-pressed={fulfilment === value}
                    className={`rounded-2xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
                      fulfilment === value ? "border-gold bg-choco" : "border-choco-line hover:border-gold/60"
                    }`}
                    data-testid={testid}
                  >
                    <Icon className="h-5 w-5 text-gold" aria-hidden="true" />
                    <p className="mt-2 font-semibold text-cream-text">{title}</p>
                    <p className="mt-0.5 text-xs text-cream-muted">{sub}</p>
                  </button>
                ))}
              </div>

              {fulfilment === "delivery" && (
                <div className="mt-5" data-testid="postcode-check-panel">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="co-line1" className={labelClass}>Address line 1 *</label>
                      <input id="co-line1" value={address.line1} onChange={(e) => setAddress((a) => ({ ...a, line1: e.target.value }))} className={inputClass} data-testid="address-line1-input" />
                    </div>
                    <div>
                      <label htmlFor="co-line2" className={labelClass}>Address line 2</label>
                      <input id="co-line2" value={address.line2} onChange={(e) => setAddress((a) => ({ ...a, line2: e.target.value }))} className={inputClass} data-testid="address-line2-input" />
                    </div>
                    <div>
                      <label htmlFor="co-city" className={labelClass}>Town / city *</label>
                      <input id="co-city" value={address.city} onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))} className={inputClass} data-testid="address-city-input" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label htmlFor="co-postcode" className={labelClass}>Delivery postcode *</label>
                    <div className="flex gap-3">
                      <input
                        id="co-postcode"
                        value={address.postcode}
                        onChange={(e) => { setAddress((a) => ({ ...a, postcode: e.target.value })); setPostcodeStatus(null); }}
                        placeholder="e.g. B96 0AA"
                        className={`${inputClass} max-w-48 uppercase`}
                        data-testid="checkout-postcode-input"
                      />
                      <button
                        type="button"
                        onClick={checkPostcode}
                        disabled={busy || !address.postcode.trim()}
                        className="rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
                        data-testid="check-postcode-button"
                      >
                        {busy ? "Checking…" : "Check delivery"}
                      </button>
                    </div>
                    {postcodeStatus && (
                      <div
                        className={`mt-3 rounded-2xl border p-4 text-sm ${
                          postcodeStatus.eligible ? "border-gold/50 bg-choco text-cream-text" : "border-amber-500/50 bg-amber-500/10 text-amber-200"
                        }`}
                        role="status"
                        data-testid="postcode-result"
                      >
                        <p>{postcodeStatus.message}</p>
                        {postcodeStatus.reason === "by_request" && (
                          <p className="mt-1" data-testid="delivery-byrequest-contact">
                            Email <a href={`mailto:${business.email}`} className="font-bold text-gold hover:underline">{business.email}</a> or call{" "}
                            <a href={`tel:${(business.phone || "").replace(/\s/g, "")}`} className="font-bold text-gold hover:underline">{business.phone}</a>.
                          </p>
                        )}
                        {postcodeStatus.eligible && (
                          <p className="mt-1 font-bold" data-testid="delivery-fee-text">
                            {postcodeStatus.fee_pence === 0 ? "Free delivery" : `Delivery fee: ${gbp(postcodeStatus.fee_pence)}`}
                          </p>
                        )}
                        {!postcodeStatus.eligible && (
                          <button type="button" onClick={() => setFulfilment("collection")} className="mt-2 font-bold text-gold hover:underline" data-testid="switch-to-collection-link">
                            Choose free collection instead
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          </Reveal>

          <Reveal>
            <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="checkout-slots">
              <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-cream-text">
                <CalendarDays className="h-5 w-5 text-gold" aria-hidden="true" />
                Choose your {fulfilment} slot
              </h2>
              {!slotsData && <p className="mt-4 text-sm text-cream-muted" data-testid="slots-loading">Loading dates…</p>}
              {slotsData && slotsData.weeks.length === 0 && fulfilment === "delivery" && (
                <div className="mt-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="no-delivery-slots-message">
                  <p>Delivery isn't available to book for the coming week — the delivery day is chosen and published each week.</p>
                  <button type="button" onClick={() => setFulfilment("collection")} className="mt-2 font-bold text-gold hover:underline" data-testid="no-slots-switch-collection">
                    Choose free collection instead
                  </button>
                </div>
              )}
              {slotsData && slotsData.weeks.length === 0 && fulfilment === "collection" && (
                <p className="mt-4 text-sm text-cream-muted" data-testid="no-slots-message">No dates are available yet — please check back soon.</p>
              )}
              {slotsData?.weeks.map((week) => (
                <div key={week.week_start_date} className="mt-5" data-testid={`slot-week-${week.week_start_date}`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-gold">
                    Week of {dayFmt.format(new Date(week.week_start_date + "T12:00:00"))}
                  </p>
                  {!week.open && (
                    <p className="mt-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid={`week-closed-${week.week_start_date}`}>
                      Ordering for this week has closed (deadline was {cutoffFmt.format(new Date(week.cutoff_utc))}).
                      {slotsData.next_open_week && slotsData.next_open_week !== week.week_start_date && " Next available week shown below."}
                    </p>
                  )}
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {week.slots.map((slot) => (
                      <button
                        key={slot.id}
                        type="button"
                        disabled={!slot.available}
                        onClick={() => chooseSlot(slot.id)}
                        aria-pressed={slotId === slot.id}
                        className={`rounded-2xl border-2 px-4 py-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus disabled:cursor-not-allowed disabled:opacity-40 ${
                          slotId === slot.id ? "border-gold bg-choco" : "border-choco-line hover:border-gold/60"
                        }`}
                        data-testid={`slot-option-${slot.id}`}
                      >
                        <span className="block font-semibold text-cream-text">{dayFmt.format(new Date(slot.date + "T12:00:00"))}</span>
                        <span className="mt-0.5 block text-xs text-cream-muted">
                          {slot.start_time}–{slot.end_time} · {slot.remaining > 0 ? `${slot.remaining} space${slot.remaining === 1 ? "" : "s"} left` : "Fully booked"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          </Reveal>

          <Reveal>
            <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="payment-method-section">
              <h2 className="font-serif text-xl font-semibold text-cream-text">Payment</h2>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("stripe")}
                  aria-pressed={paymentMethod === "stripe"}
                  className={`rounded-2xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
                    paymentMethod === "stripe" ? "border-gold bg-choco" : "border-choco-line hover:border-gold/60"
                  }`}
                  data-testid="payment-method-stripe"
                >
                  <CreditCard className="h-5 w-5 text-gold" aria-hidden="true" />
                  <p className="mt-2 font-semibold text-cream-text">Pay online now</p>
                  <p className="mt-0.5 text-xs text-cream-muted">Card, Apple Pay or Google Pay where supported</p>
                </button>
                {fulfilment === "collection" && (
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("on_collection")}
                    aria-pressed={paymentMethod === "on_collection"}
                    className={`rounded-2xl border-2 p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
                      paymentMethod === "on_collection" ? "border-gold bg-choco" : "border-choco-line hover:border-gold/60"
                    }`}
                    data-testid="payment-method-on-collection"
                  >
                    <HandCoins className="h-5 w-5 text-gold" aria-hidden="true" />
                    <p className="mt-2 font-semibold text-cream-text">Pay on collection</p>
                    <p className="mt-0.5 text-xs text-cream-muted">Pay when you pick up — cash or card</p>
                  </button>
                )}
              </div>
            </section>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-2">
          <aside className="rounded-3xl bg-cream p-6 text-cream-darktext lg:sticky lg:top-24" data-testid="order-summary">
            <h2 className="font-serif text-xl font-semibold">Order summary</h2>
            <ul className="mt-4 space-y-3" data-testid="summary-items">
              {(quote?.items || []).map((item, i) => (
                <li key={i} className="flex justify-between gap-3 text-sm">
                  <span>
                    {item.qty} × {item.name}
                    {Object.keys(item.option_values || {}).length > 0 && (
                      <span className="block text-xs text-cream-darkmuted">{Object.values(item.option_values).join(" · ")}</span>
                    )}
                  </span>
                  <span className="font-semibold">{gbp(item.line_total_pence)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-1.5 border-t border-cream-darkmuted/20 pt-4 text-sm">
              <p className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">{gbp(quote?.subtotal_pence || 0)}</span></p>
              <p className="flex justify-between">
                <span>{fulfilment === "delivery" ? "Delivery" : "Collection"}</span>
                <span data-testid="summary-delivery-fee">{fulfilment === "collection" ? "Free" : gbp(quote?.delivery_fee_pence || 0)}</span>
              </p>
              <p className="flex justify-between pt-2 font-serif text-xl font-bold"><span>Total</span><span data-testid="summary-total">{gbp(quote?.total_pence || 0)}</span></p>
            </div>

            {allAllergens.length > 0 && (
              <p className="mt-4 rounded-xl bg-choco p-3 text-xs capitalize leading-relaxed text-cream-text" data-testid="summary-allergens">
                Allergens in this order: {allAllergens.join(", ")}
              </p>
            )}
            {allMayContain.length > 0 && (
              <p className="mt-2 rounded-xl bg-choco p-3 text-xs capitalize leading-relaxed text-amber-300" data-testid="summary-may-contain">
                May contain: {allMayContain.join(", ")}
              </p>
            )}

            <div className="mt-5 space-y-3">
              <div className="flex items-start gap-2.5">
                <Checkbox id="co-allergen-ack" checked={allergenAck} onCheckedChange={(v) => setAllergenAck(v === true)} className="mt-0.5 border-cream-darkmuted/40 data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid="allergen-ack-checkbox" />
                <label htmlFor="co-allergen-ack" className="text-xs leading-relaxed text-cream-darkmuted">
                  I have read the allergen information and understand these bakes are made in a kitchen that handles common allergens. *
                </label>
              </div>
              <div className="flex items-start gap-2.5">
                <Checkbox id="co-marketing" checked={marketing} onCheckedChange={(v) => setMarketing(v === true)} className="mt-0.5 border-cream-darkmuted/40 data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid="marketing-consent-checkbox" />
                <label htmlFor="co-marketing" className="text-xs leading-relaxed text-cream-darkmuted">
                  I'd like occasional emails about new bakes and offers (optional).
                </label>
              </div>
            </div>

            {blockers.length > 0 && (
              <div className="mt-4 space-y-1.5" data-testid="checkout-blockers">
                {blockers.map((b, i) => (
                  <p key={i} className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-800" data-testid={`blocker-${i}`}>{b.message}</p>
                ))}
              </div>
            )}

            <button
              type="button"
              disabled={!formReady || busy}
              onClick={placeOrder}
              className="mt-6 w-full rounded-full bg-gold px-8 py-3.5 text-base font-bold text-choco transition-colors hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus active:scale-[0.98]"
              data-testid="pay-button"
            >
              {busy ? "Placing order…" : paymentMethod === "on_collection" ? "Place Order — Pay on Collection" : "Continue to Secure Payment"}
            </button>
            {!allergenAck && (
              <p className="mt-2 text-center text-xs text-cream-darkmuted" data-testid="allergen-ack-hint">
                Please tick the allergen acknowledgement to continue.
              </p>
            )}
          </aside>
        </Reveal>
      </div>
    </div>
  );
}
